import { useMyVariables } from '@/hooks/queries/variables/variables-v2';
import type { MyVariable } from '@/types/variables';
import { useUserStore } from '@/store/use-user-store';
import {
  isHiddenUserProfileVariable,
  normalizeProfileVariableName,
} from '@/utils/soulmap-profile';
import { useEffect, useMemo } from 'react';

export function getMyVariablesList(data: unknown): MyVariable[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'results' in data) {
    const results = (data as { results?: MyVariable[] }).results;
    return Array.isArray(results) ? results : [];
  }
  return [];
}

/** Variables rendered as fields in the User Profile form. */
export function getProfileFormVariables(data: unknown): MyVariable[] {
  return getMyVariablesList(data).filter(
    (variable) => !isHiddenUserProfileVariable(variable)
  );
}

/**
 * Normalized names of the User Profile form fields. My Memories excludes these
 * so the user does not see the same value in both places (LE-277).
 */
export function getProfileFormVariableNames(data: unknown): Set<string> {
  return new Set(
    getProfileFormVariables(data).map((variable) =>
      normalizeProfileVariableName(variable.name)
    )
  );
}

export function normalizeProfileVariableValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(',').trim();
  return String(value).trim();
}

function resolveProfileVariableName(
  variablesData: unknown,
  variableName: 'first_name' | 'last_name',
  fallbackName?: string | null,
  defaultValue = ''
): string {
  const variables = getMyVariablesList(variablesData);
  const profileValue = normalizeProfileVariableValue(
    variables.find((variable) => variable.name === variableName)?.value
  );

  if (profileValue) {
    return profileValue;
  }

  const fallback = fallbackName?.trim();
  if (fallback) {
    return fallback;
  }

  return defaultValue;
}

/** Profile variable first, then user-management record — matches prompt decode (LE-200). */
export function resolveProfileFirstName(
  variablesData: unknown,
  fallbackFirstName?: string | null
): string {
  return resolveProfileVariableName(
    variablesData,
    'first_name',
    fallbackFirstName,
    'Guest'
  );
}

/** Profile variable first, then user-management record. */
export function resolveProfileLastName(
  variablesData: unknown,
  fallbackLastName?: string | null
): string {
  return resolveProfileVariableName(
    variablesData,
    'last_name',
    fallbackLastName,
    ''
  );
}

export function resolveProfileDisplayNames(
  variablesData: unknown,
  user?: { first_name?: string | null; last_name?: string | null } | null
) {
  return {
    firstName: resolveProfileFirstName(variablesData, user?.first_name),
    lastName: resolveProfileLastName(variablesData, user?.last_name),
  };
}

export function useProfileDisplayName() {
  const user = useUserStore((state) => state.user);
  const cachedDisplayFirstName = useUserStore((state) => state.displayFirstName);
  const cachedDisplayLastName = useUserStore((state) => state.displayLastName);
  const setDisplayFirstName = useUserStore((state) => state.setDisplayFirstName);
  const setDisplayLastName = useUserStore((state) => state.setDisplayLastName);
  const { data: myVariables, isFetched } = useMyVariables();

  const resolved = useMemo(() => {
    if (!isFetched) return null;
    return resolveProfileDisplayNames(myVariables, user);
  }, [isFetched, myVariables, user]);

  useEffect(() => {
    if (resolved) {
      setDisplayFirstName(resolved.firstName);
      setDisplayLastName(resolved.lastName);
    }
  }, [resolved, setDisplayFirstName, setDisplayLastName]);

  const firstName = resolved?.firstName ?? cachedDisplayFirstName;
  const lastName = resolved?.lastName ?? cachedDisplayLastName ?? '';

  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return {
    firstName,
    lastName,
    fullName,
    isReady: firstName !== null && firstName !== undefined,
  };
}

export function useProfileDisplayFirstName() {
  const { firstName, isReady } = useProfileDisplayName();

  return {
    greetingFirstName: firstName,
    isGreetingReady: isReady,
  };
}
